import React from 'react'
import {
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@redux/store'
import { getListMyChiNhanh, getListMyDuAn } from '@redux/ducks/common/user.Ducks'
import * as userApi from '@apis/common/user.Api'
import {
  getBranchInfo,
  getProjectInfo,
  saveAccessToken,
  saveBranchInfo,
  saveProjectInfo,
  saveUserInfo,
} from '@utils/userUtils'

type FlatBranchOption = {
  id: number
  duAnId: number
  ma: string
  ten: string
  duAn: { id: number; ma: string; ten: string }
}

const HEADER_BRANCH_FRAME_SX = {
  boxSizing: 'border-box',
  border: '1px solid rgba(255, 255, 255, 0.22)',
  borderRadius: '6px',
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  minHeight: 40,
  display: 'flex',
  alignItems: 'center',
} as const

const menuDuAnTextSx = {
  display: 'block',
  fontSize: '0.6875rem',
  fontWeight: 500,
  lineHeight: 1.3,
  letterSpacing: '0.02em',
  color: 'text.secondary',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const

const menuCnTextSx = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 700,
  lineHeight: 1.35,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const

function flattenDuAnList(duAnList: any[]): FlatBranchOption[] {
  const out: FlatBranchOption[] = []
  duAnList.forEach((item) => {
    if (!item) return
    const duAnMeta = { id: Number(item.id), ma: String(item.ma ?? ''), ten: String(item.ten ?? '') }
    if (Array.isArray(item.userNhomQuyens) && item.userNhomQuyens.length > 0) {
      item.userNhomQuyens.forEach((unq: any) => {
        const c = unq?.chiNhanh
        if (!c?.id) return
        out.push({
          id: Number(c.id),
          duAnId: duAnMeta.id,
          ma: String(c.ma ?? ''),
          ten: String(c.ten ?? ''),
          duAn: duAnMeta,
        })
      })
    } else if (Array.isArray(item.chiNhanhs) && item.chiNhanhs.length > 0) {
      item.chiNhanhs.forEach((c: any) => {
        if (!c?.id) return
        out.push({
          id: Number(c.id),
          duAnId: duAnMeta.id,
          ma: String(c.ma ?? ''),
          ten: String(c.ten ?? ''),
          duAn: duAnMeta,
        })
      })
    }
  })
  return out
}

/** Branch switcher — UI port từ ytl-logistics HeaderBranchSwitcher. */
const HeaderBranchSwitcher: React.FC = () => {
  const theme = useTheme()
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'))
  const dispatch: AppDispatch = useDispatch()
  const duAnCuaToiList = useSelector((state: RootState) => state.user.duAnCuaToiList)
  const branchInfo = getBranchInfo()
  const projectInfo = getProjectInfo()

  const [branchMenuAnchorEl, setBranchMenuAnchorEl] = React.useState<HTMLElement | null>(null)
  const [branchSwitching, setBranchSwitching] = React.useState(false)

  React.useEffect(() => {
    dispatch(getListMyDuAn({}))
    dispatch(getListMyChiNhanh({}))
  }, [dispatch])

  const branchOptions = React.useMemo(() => {
    const data = Array.isArray(duAnCuaToiList?.data)
      ? duAnCuaToiList.data
      : Array.isArray(duAnCuaToiList)
        ? duAnCuaToiList
        : []
    return flattenDuAnList(data)
  }, [duAnCuaToiList])

  const branchListLoading = Boolean(duAnCuaToiList?.loading)

  const closeBranchMenu = React.useCallback(() => setBranchMenuAnchorEl(null), [])

  const handleChangeBranch = React.useCallback(
    async (item: FlatBranchOption) => {
      if (item.id === Number(branchInfo?.id) && item.duAnId === Number(projectInfo?.id)) {
        closeBranchMenu()
        return
      }
      setBranchSwitching(true)
      closeBranchMenu()
      try {
        const res = await userApi.changeBranch(item.duAnId, item.id)
        saveAccessToken(res.data.access_token)
        saveBranchInfo(JSON.stringify(res.data.chiNhanh))
        saveProjectInfo(JSON.stringify(res.data.duAn))
        const resUser = await userApi.getUserInfo()
        saveUserInfo(JSON.stringify(resUser.data))
        window.location.reload()
      } catch {
        setBranchSwitching(false)
      }
    },
    [branchInfo?.id, projectInfo?.id, closeBranchMenu],
  )

  if (branchListLoading && branchOptions.length === 0) {
    return (
      <Box
        sx={{
          ...HEADER_BRANCH_FRAME_SX,
          mr: 0.25,
          px: { xs: 1, sm: 1.25 },
          justifyContent: 'center',
          minWidth: 40,
        }}
      >
        <CircularProgress size={18} sx={{ color: 'inherit' }} />
      </Box>
    )
  }
  if (branchOptions.length === 0) return null

  const open = Boolean(branchMenuAnchorEl)
  const branchDisplay =
    [projectInfo?.ma, branchInfo?.ma].filter(Boolean).join(' · ') ||
    branchInfo?.ten ||
    'Chi nhánh'

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="Đổi chi nhánh"
        title={branchDisplay}
        onClick={(e) => setBranchMenuAnchorEl(e.currentTarget)}
        disabled={branchSwitching}
        sx={{
          ...HEADER_BRANCH_FRAME_SX,
          mr: 0.25,
          px: { xs: 0.75, sm: 1.25 },
          gap: 0.5,
          maxWidth: { xs: 168, sm: 280, md: 400 },
          minWidth: 0,
          cursor: branchSwitching ? 'wait' : 'pointer',
          color: 'inherit',
          font: 'inherit',
          textAlign: 'left',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.14)',
          },
          '&:disabled': { opacity: 0.75 },
        }}
      >
        {branchSwitching ? (
          <CircularProgress size={16} sx={{ color: 'inherit', flexShrink: 0 }} />
        ) : (
          <BusinessOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
        )}
        {isMdUp ? (
          <Typography
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: { xs: '0.8125rem', sm: '0.9375rem' },
              fontWeight: 700,
              lineHeight: 1.25,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'inherit',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {branchDisplay}
          </Typography>
        ) : null}
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            flexShrink: 0,
            opacity: 0.85,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 120ms ease',
          }}
        />
      </Box>
      <Menu
        anchorEl={branchMenuAnchorEl}
        open={open}
        onClose={closeBranchMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.75,
            minWidth: 220,
            maxWidth: 360,
            maxHeight: 320,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 28px rgba(15, 23, 42, 0.16)',
          },
        }}
        MenuListProps={{ dense: true, sx: { py: 0.5 } }}
      >
        {branchOptions.map((opt) => {
          const isActive =
            opt.id === Number(branchInfo?.id) && opt.duAnId === Number(projectInfo?.id)
          const duAnLabel = opt.duAn.ten?.trim() || opt.duAn.ma?.trim()
          const cnLabel = opt.ten.trim() || opt.ma.trim()
          return (
            <MenuItem
              key={`${opt.duAnId}-${opt.id}`}
              selected={isActive}
              onClick={() => void handleChangeBranch(opt)}
              disabled={branchSwitching}
              sx={{ py: 0.75, minHeight: 40, alignItems: 'flex-start' }}
            >
              <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                {duAnLabel ? (
                  <Typography component="span" sx={menuDuAnTextSx}>
                    {duAnLabel}
                  </Typography>
                ) : null}
                <Typography component="span" sx={menuCnTextSx}>
                  {cnLabel}
                </Typography>
              </Box>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

export default HeaderBranchSwitcher
